import React from 'react';

import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';

import { ExtensionMode } from '@atlassian/company-hub-common';
import { CARDS_EXTENSION_TYPE } from '@atlassian/cards-extension';
import type { CardsExtensionParameters } from '@atlassian/cards-extension';
import {
	SEARCH_EXTENSION_TYPE,
	type SearchExtensionParameters,
} from '@atlassian/custom-search-extension';

import { LoadablePaint } from '@confluence/loadable';

import { spotlightExtensionType } from '../spotlight/spotlightConstants';
import type { SpotlightParameters } from '../spotlight/spotlightTypes';
import { carouselExtensionType } from '../carousel/carouselConstants';
import { CarouselLoader } from '../carousel/view/CarouselLoader';
import { hubElementPlaceholderExtensionType } from '../hub-element-placeholder/hubElementPlaceholderConstants';
import { linksMenuExtensionType } from '../links-menu/linksMenuExtensionType';
import { LinksMenuLoader } from '../links-menu/view/LinksMenuLoader';
import type { LinksMenuParameters } from '../links-menu/linksMenuTypes';
import { SpotlightLoader } from '../spotlight/view/SpotlightLoader';
import { hubSectionExtensionType } from '../section/sectionConstants';
import { ConfluenceDependenciesProvider } from '../platform-props-providers/ConfluenceDependenciesProvider';
import type { CarouselParameters } from '../carousel/carouselTypes';

export const LinkCardsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-LinkCards" */ '../link-cards/LinkCards'))
			.LinkCards,
});

const PlatformSearchExtensionLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PlatformSearchExtension" */ '@atlassian/custom-search-extension'
			)
		).CustomSearchExtension,
});

type getRendererCustomSitesExtensionHandlersProps = {
	contentId: string | undefined;
};

export const getRendererCustomSitesExtensionHandlers = ({
	contentId,
}: getRendererCustomSitesExtensionHandlersProps): ExtensionHandlers => {
	const extensionHandlers: ExtensionHandlers = {
		[CARDS_EXTENSION_TYPE]: () => <></>,
		[carouselExtensionType]: () => <></>,
		[linksMenuExtensionType]: () => <></>,
		[hubElementPlaceholderExtensionType]: () => <></>,
		[spotlightExtensionType]: () => <></>,
		[hubSectionExtensionType]: () => <></>,
		[SEARCH_EXTENSION_TYPE]: () => <></>,
	};

	if (contentId) {
		extensionHandlers[CARDS_EXTENSION_TYPE] = (
			extension: ExtensionParams<CardsExtensionParameters>,
		) => (
			<LinkCardsLoader
				extensionNode={extension}
				contentId={contentId}
				mode={ExtensionMode.RENDERER}
			/>
		);
		extensionHandlers[carouselExtensionType] = (extension: ExtensionParams<CarouselParameters>) => (
			<CarouselLoader extensionNode={extension} contentId={contentId} />
		);
		extensionHandlers[linksMenuExtensionType] = (
			extension: ExtensionParams<LinksMenuParameters>,
		) => <LinksMenuLoader extensionNode={extension} contentId={contentId} />;
		extensionHandlers[spotlightExtensionType] = (
			extension: ExtensionParams<SpotlightParameters>,
		) => <SpotlightLoader extensionNode={extension} contentId={contentId} />;
		extensionHandlers[hubElementPlaceholderExtensionType] = () => null;
		extensionHandlers[SEARCH_EXTENSION_TYPE] = (
			extension: ExtensionParams<SearchExtensionParameters>,
		) => (
			<PlatformSearchExtensionLoader
				mode={ExtensionMode.RENDERER}
				contentId={contentId}
				extensionNode={extension}
				propsProvider={ConfluenceDependenciesProvider}
			/>
		);
	}
	return extensionHandlers;
};
